import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import {
  ShoppingCart, Package, AlertTriangle, Check, Loader, Download,
  Building2, Plus, PackageCheck
} from 'lucide-react';
import { useModal } from './AppModal';
import { useTheme } from './ThemeContext';

export default function ReorderListPage({ companyId }) {
  const { colors } = useTheme();
  const { showSuccess, showError, showConfirm } = useModal();

  const [loading, setLoading] = useState(true);
  const [reorderItems, setReorderItems] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [groupByVendor, setGroupByVendor] = useState(true);
  const [defaultLocationId, setDefaultLocationId] = useState(null);
  const [restocking, setRestocking] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (companyId) loadReorderList();
  }, [companyId]);

  const loadReorderList = async () => {
    try {
      setLoading(true);

      const { data: defaultLoc } = await supabaseData
        .from('locations')
        .select('id, name')
        .eq('company_id', companyId)
        .eq('is_default', true)
        .single();

      if (!defaultLoc) { setReorderItems([]); return; }
      setDefaultLocationId(defaultLoc.id);

      // Load items with vendor info
      const { data: items } = await supabaseData
        .from('inventory_items')
        .select('id, name, category, part_number, reorder_point, reorder_quantity, unit_cost, unit_of_measure, vendor_id, vendors(id, name)')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .gt('reorder_point', 0)
        .order('name');

      if (!items || items.length === 0) { setReorderItems([]); return; }

      const { data: stock } = await supabaseData
        .from('inventory_stock')
        .select('inventory_item_id, quantity_on_hand')
        .eq('location_id', defaultLoc.id)
        .in('inventory_item_id', items.map(i => i.id));

      const stockMap = {};
      (stock || []).forEach(s => { stockMap[s.inventory_item_id] = s.quantity_on_hand || 0; });

      const needsReorder = items
        .map(item => ({
          ...item,
          vendor_name: item.vendors?.name || null,
          quantity_on_hand: stockMap[item.id] || 0,
          order_quantity: item.reorder_quantity || Math.max(1, (item.reorder_point || 0) * 2 - (stockMap[item.id] || 0))
        }))
        .filter(item => item.quantity_on_hand <= item.reorder_point)
        .sort((a, b) => {
          const ratioA = a.quantity_on_hand / (a.reorder_point || 1);
          const ratioB = b.quantity_on_hand / (b.reorder_point || 1);
          return ratioA - ratioB;
        });

      setReorderItems(needsReorder);
    } catch (error) {
      console.error('Error loading reorder list:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleChecked = (itemId) => {
    setCheckedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const getTotalCost = () => {
    return reorderItems
      .filter(item => !checkedItems[item.id])
      .reduce((sum, item) => sum + (item.order_quantity * (item.unit_cost || 0)), 0);
  };

  const copyToClipboard = () => {
    const unchecked = reorderItems.filter(item => !checkedItems[item.id]);
    if (groupByVendor) {
      const grouped = groupItems(unchecked);
      const lines = [];
      Object.entries(grouped).forEach(([vendor, items]) => {
        lines.push(`--- ${vendor} ---`);
        items.forEach(item => {
          lines.push(`  ${item.order_quantity}x ${item.name}${item.part_number ? ` (${item.part_number})` : ''}`);
        });
        lines.push('');
      });
      navigator.clipboard.writeText(lines.join('\n'));
    } else {
      const text = unchecked.map(item =>
        `${item.order_quantity}x ${item.name}${item.part_number ? ` (${item.part_number})` : ''}`
      ).join('\n');
      navigator.clipboard.writeText(text);
    }
    showSuccess('Reorder list copied to clipboard');
  };

  // Restock confirmation - marks checked items as restocked
  const handleRestockChecked = async () => {
    const itemsToRestock = reorderItems.filter(item => checkedItems[item.id]);
    if (itemsToRestock.length === 0) {
      showError('Check items you\'ve restocked first');
      return;
    }

    const confirmed = await showConfirm(
      `Mark ${itemsToRestock.length} item${itemsToRestock.length !== 1 ? 's' : ''} as restocked? This will add the reorder quantity to inventory.`
    );
    if (!confirmed) return;

    setRestocking(true);
    try {
      for (const item of itemsToRestock) {
        // Get current stock
        const { data: currentStock } = await supabase
          .from('inventory_stock')
          .select('id, quantity_on_hand')
          .eq('inventory_item_id', item.id)
          .eq('location_id', defaultLocationId)
          .single();

        const currentQty = currentStock?.quantity_on_hand || 0;
        const newQty = currentQty + item.order_quantity;

        if (currentStock) {
          await supabase
            .from('inventory_stock')
            .update({ quantity_on_hand: newQty, updated_at: new Date().toISOString() })
            .eq('id', currentStock.id);
        } else {
          await supabase
            .from('inventory_stock')
            .insert({
              company_id: companyId,
              inventory_item_id: item.id,
              location_id: defaultLocationId,
              quantity_on_hand: newQty
            });
        }

        // Log transaction
        await supabase
          .from('inventory_transactions')
          .insert({
            company_id: companyId,
            inventory_item_id: item.id,
            location_id: defaultLocationId,
            transaction_type: 'purchase',
            quantity_change: item.order_quantity,
            quantity_before: currentQty,
            quantity_after: newQty,
            notes: `Restocked from reorder list (+${item.order_quantity})`
          });
      }

      showSuccess(`${itemsToRestock.length} item${itemsToRestock.length !== 1 ? 's' : ''} restocked`);
      setCheckedItems({});
      loadReorderList();
    } catch (error) {
      console.error('Restock error:', error);
      showError('Failed to restock: ' + error.message);
    } finally {
      setRestocking(false);
    }
  };

  const groupItems = (items) => {
    const groups = {};
    items.forEach(item => {
      const vendorKey = item.vendor_name || 'No Vendor';
      if (!groups[vendorKey]) groups[vendorKey] = [];
      groups[vendorKey].push(item);
    });
    return groups;
  };

  const checkedCount = Object.values(checkedItems).filter(Boolean).length;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh', color: colors.textMuted }}>
        <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '1rem' }}>Loading reorder list...</span>
      </div>
    );
  }

  const renderItem = (item) => {
    const isChecked = checkedItems[item.id];
    return (
      <div
        key={item.id}
        onClick={() => toggleChecked(item.id)}
        style={{
          background: isChecked ? `${colors.success}10` : colors.bgCard,
          borderRadius: '10px',
          padding: '0.85rem 1.25rem',
          border: `1px solid ${isChecked ? colors.success + '40' : colors.border}`,
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '0.5rem' : '1rem',
          cursor: 'pointer',
          opacity: isChecked ? 0.6 : 1,
          transition: 'opacity 0.2s'
        }}
      >
        <div style={{
          width: '24px', height: '24px', borderRadius: '6px',
          border: `2px solid ${isChecked ? colors.success : colors.border}`,
          background: isChecked ? colors.success : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }}>
          {isChecked && <Check size={14} color="#fff" />}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            color: colors.text, fontWeight: 'bold',
            textDecoration: isChecked ? 'line-through' : 'none'
          }}>
            {item.order_quantity}x {item.name}
          </div>
          <div style={{ fontSize: '0.8rem', color: colors.textMuted, display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {item.part_number && <span>PN: {item.part_number}</span>}
            {item.category && <span>{item.category}</span>}
            {!groupByVendor && item.vendor_name && (
              <span style={{ color: colors.accent }}>{item.vendor_name}</span>
            )}
            <span style={{ color: item.quantity_on_hand === 0 ? colors.error : colors.warning }}>
              {item.quantity_on_hand === 0 ? 'OUT OF STOCK' : `${item.quantity_on_hand} on hand`}
            </span>
          </div>
        </div>

        {item.unit_cost > 0 && (
          <div style={{ color: colors.textMuted, fontSize: '0.9rem', flexShrink: 0 }}>
            ${(item.order_quantity * item.unit_cost).toFixed(2)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: isMobile ? '1rem' : '2rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div>
          <h1 style={{ margin: 0, color: colors.text, fontSize: isMobile ? '1.5rem' : '2rem' }}>
            <ShoppingCart size={isMobile ? 24 : 32} style={{ marginRight: '0.75rem', verticalAlign: 'middle' }} />
            Reorder List
          </h1>
          <p style={{ margin: '0.5rem 0 0', color: colors.textMuted }}>
            {reorderItems.length} item{reorderItems.length !== 1 ? 's' : ''} need restocking
          </p>
        </div>

        {reorderItems.length > 0 && (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {checkedCount > 0 && (
              <button
                onClick={handleRestockChecked}
                disabled={restocking}
                style={{
                  padding: '0.75rem 1.25rem',
                  background: colors.success,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: restocking ? 'not-allowed' : 'pointer',
                  opacity: restocking ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <PackageCheck size={18} />
                {restocking ? 'Restocking...' : `Restock ${checkedCount} Item${checkedCount !== 1 ? 's' : ''}`}
              </button>
            )}
            <button
              onClick={copyToClipboard}
              style={{
                padding: '0.75rem 1.25rem',
                background: colors.accent,
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <Download size={18} />
              Copy List
            </button>
          </div>
        )}
      </div>

      {reorderItems.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: colors.textMuted }}>
          <Check size={64} style={{ marginBottom: '1rem', opacity: 0.3, color: colors.success }} />
          <h2 style={{ color: colors.text, margin: '0 0 0.5rem' }}>All stocked up!</h2>
          <p>No items are below their reorder point.</p>
        </div>
      ) : (
        <>
          {/* Summary + Controls */}
          <div style={{
            background: `${colors.warning}15`,
            border: `1px solid ${colors.warning}40`,
            borderRadius: '12px',
            padding: '1rem 1.25rem',
            marginBottom: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <AlertTriangle size={20} color={colors.warning} />
              <span style={{ color: colors.warning, fontWeight: 'bold' }}>
                {reorderItems.filter(i => !checkedItems[i.id]).length} remaining
              </span>
              {getTotalCost() > 0 && (
                <span style={{ color: colors.text, fontWeight: 'bold' }}>
                  Est. cost: ${getTotalCost().toFixed(2)}
                </span>
              )}
            </div>

            {/* Group toggle */}
            <button
              onClick={() => setGroupByVendor(!groupByVendor)}
              style={{
                padding: '0.4rem 0.75rem',
                background: groupByVendor ? colors.accent : colors.bgInput,
                border: `1px solid ${groupByVendor ? colors.accent : colors.border}`,
                borderRadius: '6px',
                color: groupByVendor ? '#fff' : colors.textMuted,
                fontSize: '0.8rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}
            >
              <Building2 size={14} />
              Group by Vendor
            </button>
          </div>

          {/* Items - grouped or flat */}
          {groupByVendor ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {Object.entries(groupItems(reorderItems)).map(([vendorName, items]) => (
                <div key={vendorName}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.75rem',
                    paddingBottom: '0.5rem',
                    borderBottom: `1px solid ${colors.border}`
                  }}>
                    <Building2 size={16} color={vendorName === 'No Vendor' ? colors.textMuted : colors.accent} />
                    <span style={{
                      color: vendorName === 'No Vendor' ? colors.textMuted : colors.text,
                      fontWeight: 'bold',
                      fontSize: '1rem'
                    }}>
                      {vendorName}
                    </span>
                    <span style={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                      ({items.length} item{items.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {items.map(renderItem)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {reorderItems.map(renderItem)}
            </div>
          )}

          {/* Restock info */}
          <div style={{
            marginTop: '2rem',
            padding: '1rem',
            background: colors.bgCard,
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            color: colors.textMuted,
            fontSize: '0.85rem'
          }}>
            <PackageCheck size={16} style={{ marginRight: '0.5rem', verticalAlign: 'text-bottom' }} />
            Check items as you receive them, then click <strong style={{ color: colors.success }}>Restock</strong> to automatically update inventory quantities and log the purchase.
          </div>
        </>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
